import { useMemo, type ReactNode } from 'react';

import { useCloudProviders } from '@/contexts/useCloudProviders';
import type { FilterProperty } from '@/types/advanceFilter';

import { createColumns } from '../queryBuilder/propertiesUtils';

export const useColumns = <T>(properties: FilterProperty[]) => {
  const providers = useCloudProviders();

  return useMemo(() => {
    const relevantColumns = properties.filter((x) => {
      if (!x.propertyPredicate) {
        return true;
      }

      return x.propertyPredicate(providers);
    });

    return createColumns<T>(relevantColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useFields = <T>(
  properties: FilterProperty<T>[],
  entity: T,
  onChange?: () => Promise<void>
): FormattedField[] => {
  const formattedFields = useMemo(
    () =>
      properties
        .filter((x) => x.field)
        .map((property) => ({
          id: property.name,
          group: property.group,
          title: property.field!.getTitle?.(entity) || property.displayName,
          Component: property.field!.Component,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity]
  );

  return formattedFields
    .map((field) => ({
      ...field,
      Component: field.Component({ entity, onChange }),
    }))
    .filter((field) => !!field.Component);
};

export interface FormattedField {
  id: string;
  title: ReactNode;
  group: string;
  Component: ReactNode;
}
